<template>
  <div class="h-full overflow-hidden p-4">
    <div class="flex h-full min-h-0 flex-col">
      <div
        class="mx-1 mb-4 flex shrink-0 items-center justify-between"
      >
        <PageTitle>Select Cashier</PageTitle>
      </div>

      <section
        v-if="allowLoginWithoutCashier"
        class="mb-4 shrink-0"
      >
        <ActionButtonSecondary
          :is-loading="isLoggingInAnonymously"
          class="w-full"
          @click="() => handleLogIn()"
        >
          Skip Cashier Log In
        </ActionButtonSecondary>
      </section>

      <section
        class="-mx-4 flex min-h-0 flex-1 flex-col overflow-y-auto"
      >
        <QueryLoading v-if="isLoading" />
        <QueryError v-else-if="isError">
          Failed to retrieve cashiers
        </QueryError>
        <ul
          v-else-if="cashiers.length > 0"
          class="divide-y divide-background-fg/5"
        >
          <template
            v-for="cashier in cashiers"
            :key="cashier.id"
          >
            <li>
              <button
                type="button"
                class="relative flex w-full cursor-pointer flex-row items-center space-x-3 px-8 py-3"
                @click="handleLogIn(cashier)"
              >
                <BaseInnerLoading
                  v-if="isLoggingInCashierId === cashier.id"
                />
                <div
                  class="relative flex size-12 items-center justify-center rounded-full bg-primary text-white"
                  :style="{
                    backgroundColor:
                      cashier.color || undefined,
                  }"
                >
                  {{ buildShortString(cashier.username) }}
                </div>
                <div
                  class="flex flex-1 items-center justify-start"
                >
                  <div
                    class="line-clamp-2 text-left text-sm font-semibold"
                  >
                    {{ cashier.username }}
                  </div>
                </div>
              </button>
            </li>
          </template>
        </ul>
        <div v-else class="flex flex-1 flex-col px-4">
          <p
            class="flex flex-1 items-center justify-center font-semibold text-background-fg/80"
          >
            No cashiers to display.
          </p>
          <p
            class="text-center text-sm text-background-fg/80"
          >
            Please use the Web Portal to either create a
            cashier, or enable logging in without a cashier.
          </p>
        </div>
      </section>
    </div>
  </div>

  <CashierLogInDrawer ref="cashierLogInDrawerRef" />
</template>

<script setup lang="ts">
import type { Cashier } from "@/services/sharedTypes/cashiers"
import { logInAsCashier } from "@/services/logInAsCashier"
import { useCashiers } from "@/services/cashiers/listCashiers"

const { setBaseToken } = useAuth()
const router = useRouter()
const { data: cashiers, isError, isLoading } = useCashiers()
const { isCashiersEnabled, allowLoginWithoutCashier } =
  useTenant()

const cashierLogInDrawerRef = useTemplateRef(
  "cashierLogInDrawerRef"
)

const isLoggingInAnonymously = ref(false)
const isLoggingInCashierId = ref<string | null>(null)

const handleLogIn = async (cashier?: Cashier) => {
  if (!cashier) {
    isLoggingInAnonymously.value = true

    try {
      const body = await logInAsCashier({
        cashierId: null,
        pin: null,
      })
      setBaseToken(body.accessToken)
      router.push("/")
    } catch {
      toast.error("Failed to log in anonymously")
    } finally {
      isLoggingInAnonymously.value = false
    }
  } else if (!cashier.pin) {
    isLoggingInCashierId.value = cashier.id

    try {
      const body = await logInAsCashier({
        cashierId: cashier.id,
        pin: null,
      })
      setBaseToken(body.accessToken)
      router.push("/")
    } catch {
      toast.error("Failed to log in")
    } finally {
      isLoggingInCashierId.value = null
    }
  } else {
    if (cashierLogInDrawerRef.value === null) {
      return
    }

    const { isCanceled } =
      await cashierLogInDrawerRef.value.reveal({
        cashier,
      })

    if (isCanceled) {
      return
    } else {
      router.push("/")
    }
  }
}

// Not sure if this might introduce bugs??
whenever(
  () => isCashiersEnabled.value === false,
  () => handleLogIn(),
  { immediate: true }
)
</script>
